// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AccountMessages from "@insite/content-library/Widgets/MyAccount/AccountMessages";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import StyledBox, { StyledBoxStyles } from "@turnerBlueprint/XDL/Components/Common/Box";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Changes
enum fields {
    showSignOut = "showSignOut",
}

// XNG-Changes
const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isPunchOutSession: getIsPunchOutSession(state),
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showSignOut]: boolean;
    };
}

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyAccountViewStyles {
    quickLinksGridItem?: GridItemProps;
    mainSectionGridItem?: GridItemProps;
    mainSectionGridContainer?: GridContainerProps;
    accountMessagesGridItem?: GridItemProps;
    dashboardWidgetsGridItem?: GridItemProps;
    // XNG-Changes
    pageTitle?: InjectableCss;
    quickLinksSection?: InjectableCss;
    dashboardSummary?: InjectableCss;
    accountMessages?: InjectableCss;
    container?: InjectableCss;
    mainContainer?: GridContainerProps;
    mainSection?: InjectableCss;
    welcomeAndMessagesSection?: InjectableCss;
    actionSection?: {
        wrapper?: InjectableCss;
        signOutButton?: LinkPresentationProps;
    };
    linksBox?: StyledBoxStyles;
    extraCardWraaper?: InjectableCss;
    dashboardButton?: LinkPresentationProps;
}

export const myAccountViewStyles: MyAccountViewStyles = {
    quickLinksGridItem: { width: [12, 12, 12, 2, 2] },
    mainSectionGridItem: { width: [12, 12, 12, 10, 10] },
    accountMessagesGridItem: { width: 12 },
    dashboardWidgetsGridItem: { width: 12 },
};

const styles = myAccountViewStyles;

class MyAccountView extends React.PureComponent<Props> {
    onSignOutHandler = () => {
        this.props.isPunchOutSession ? this.props.cancelPunchOut() : this.props.signOut();
    };
    render() {
        // XNG-Changes: Update structure
        return (
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.quickLinksSection}>
                    <StyledBox extendedStyles={styles.linksBox}>
                        <Link {...styles.dashboardButton} aria-label="Dashboard">
                            {translate("Dashboard")}
                        </Link>
                        <XngZone contentId={this.props.id} zoneName="NavigationList" hint="Links & Profile" />
                        {this.props.fields.showSignOut && (
                            <StyledWrapper {...styles.actionSection?.wrapper}>
                                <Link
                                    {...styles.actionSection?.signOutButton}
                                    onClick={() => {
                                        this.onSignOutHandler?.();
                                    }}
                                    aria-label="Log Out"
                                    data-test-selector="header-logOutLink"
                                >
                                    {this.props.isPunchOutSession
                                        ? translate("Cancel PunchOut")
                                        : translate("Sign Out")}
                                </Link>
                            </StyledWrapper>
                        )}
                    </StyledBox>
                    <StyledWrapper {...styles.extraCardWraaper}>
                        <XngZone contentId={this.props.id} zoneName="ExtraCards" hint="Extra Cards" />
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.mainSection}>
                    <StyledWrapper {...styles.mainContainer}>
                        <StyledWrapper {...styles.welcomeAndMessagesSection}>
                            <XngZone
                                contentId={this.props.id}
                                zoneName="MakeDashboardMyHomePage"
                                hint="Make Dashboard Checkbox"
                            />
                            <StyledWrapper {...styles.accountMessages}>
                                <AccountMessages />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.dashboardSummary}>
                            <XngZone contentId={this.props.id} zoneName="DashboardWidgets" hint="Dashboard Widgets" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const myAccountViewWidget: WidgetModule = {
    // XNG-Change
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(MyAccountView)),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
        fieldDefinitions: [
            {
                name: fields.showSignOut,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default myAccountViewWidget;
